import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Users, ArrowUpRight, Mail, UserX, UserCheck, Clock, MailCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Portal engagement widget for the investor Overview page.
 *
 * Surfaces: 3 stat pills (active this month · dormant · never signed in) +
 * an actionable list of up to 5 never-signed-in tenants with a one-click
 * Resend Welcome button each.
 *
 * Silently renders nothing when the landlord has zero tenants — no need to
 * clutter an empty-portfolio dashboard.
 */

const DAYS_ACTIVE = 30;

export default function PortalEngagementWidget() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});  // { tenantId: true } while resending
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenants");
      setTenants(data || []);
    } catch {
      // silently — widget is opt-in; don't bubble errors into the overview
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const { activeCount, dormantCount, neverCount, neverList } = useMemo(() => {
    const cutoff = Date.now() - DAYS_ACTIVE * 24 * 60 * 60 * 1000;
    let active = 0, dormant = 0, never = 0;
    const neverArr = [];
    for (const t of tenants) {
      if (!t.last_login_at) {
        never += 1;
        neverArr.push(t);
      } else {
        const ts = new Date(t.last_login_at).getTime();
        if (Number.isFinite(ts) && ts >= cutoff) active += 1;
        else dormant += 1;
      }
    }
    return {
      activeCount: active,
      dormantCount: dormant,
      neverCount: never,
      neverList: neverArr.slice(0, 5),
    };
  }, [tenants]);

  const resendWelcome = async (tenant) => {
    setBusy((s) => ({ ...s, [tenant.id]: true }));
    try {
      const { data } = await api.post(`/tenants/${tenant.id}/resend-welcome`);
      if (data.welcome_email_sent) {
        toast.success(`Welcome email sent to ${tenant.email}`);
      } else if (data.welcome_email_status === "mocked") {
        toast.message("MOCK email mode — share manually", {
          description: `${tenant.email} / ${data.temp_password}`,
        });
      } else {
        toast.warning("Email failed — share manually", {
          description: `${tenant.email} / ${data.temp_password}`,
        });
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Resend failed");
    } finally {
      setBusy((s) => ({ ...s, [tenant.id]: false }));
    }
  };

  const bulkResend = async () => {
    if (neverCount === 0) return;
    const ok = window.confirm(
      `Send a fresh welcome email to all ${neverCount} tenant${neverCount === 1 ? "" : "s"} ` +
      `who haven't signed in yet?\n\nEach will get a NEW temporary password — any old credentials ` +
      `they had will stop working.`,
    );
    if (!ok) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/tenants/bulk-resend-welcome", {});  // defaults to never_only
      const { sent, failed, total, results } = data;
      if (sent === total && total > 0) {
        toast.success(`Welcome emails sent to ${sent} tenant${sent === 1 ? "" : "s"}`);
      } else if (sent > 0 && failed > 0) {
        toast.warning(`${sent} sent, ${failed} failed`, {
          description: "Check toast history — failed rows include manual-share credentials.",
        });
        // Surface failures individually so landlord can manually share creds
        results.filter((r) => !r.welcome_email_sent).forEach((r) => {
          toast.message(`Failed: ${r.email}`, {
            description: `Manual share: ${r.email} / ${r.temp_password}`,
            duration: 15000,
          });
        });
      } else if (failed === total && total > 0) {
        toast.error(`All ${failed} emails failed to send — see fallback credentials`);
        results.forEach((r) => {
          toast.message(`${r.email}`, {
            description: `Manual share: ${r.temp_password}`,
            duration: 15000,
          });
        });
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bulk resend failed");
    } finally {
      setBulkBusy(false);
    }
  };

  if (loading) return null;
  if (tenants.length === 0) return null; // empty-portfolio: hide entirely

  const total = tenants.length;

  return (
    <section
      className="bg-white border border-slate-200 rounded-sm p-5"
      data-testid="portal-engagement-widget"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="overline text-slate-500 flex items-center gap-1.5">
            <Users className="w-3 h-3" /> Portal engagement
          </div>
          <div className="text-sm font-bold text-slate-900 mt-1" style={{ fontFamily: "Outfit" }}>
            {activeCount} of {total} tenants active this month
            {neverCount > 0 && (
              <span className="text-amber-700"> · {neverCount} never signed in</span>
            )}
          </div>
        </div>
        <Link
          to="/dashboard/tenants"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 transition-colors"
          data-testid="pew-see-all"
        >
          See all <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>

      {/* Three stat pills */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <StatPill
          label="Active"
          sub={`last ${DAYS_ACTIVE}d`}
          value={activeCount}
          tone="good"
          icon={<UserCheck className="w-3 h-3" />}
          testid="pew-active-count"
        />
        <StatPill
          label="Dormant"
          sub={`>${DAYS_ACTIVE}d ago`}
          value={dormantCount}
          tone="warn"
          icon={<Clock className="w-3 h-3" />}
          testid="pew-dormant-count"
        />
        <StatPill
          label="Never"
          sub="signed in"
          value={neverCount}
          tone={neverCount > 0 ? "bad" : "neutral"}
          icon={<UserX className="w-3 h-3" />}
          testid="pew-never-count"
        />
      </div>

      {/* Action list — only the never-signed-in cohort */}
      {neverList.length > 0 ? (
        <div>
          <div className="overline text-slate-500 mb-2 flex items-center justify-between gap-2">
            <span>One-click resend welcome</span>
            <div className="flex items-center gap-2">
              {neverCount > neverList.length && (
                <span className="text-[10px] text-slate-400 normal-case tracking-normal">
                  showing {neverList.length} of {neverCount}
                </span>
              )}
              {neverCount >= 2 && (
                <button
                  type="button"
                  onClick={bulkResend}
                  disabled={bulkBusy}
                  data-testid="pew-bulk-resend"
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50 transition-colors"
                  title={`Resend welcome email to all ${neverCount} never-signed-in tenants at once`}
                >
                  <MailCheck className="w-3 h-3" />
                  {bulkBusy ? `Sending to ${neverCount}…` : `Resend to all ${neverCount}`}
                </button>
              )}
            </div>
          </div>
          <ul className="space-y-1.5" data-testid="pew-never-list">
            {neverList.map((t) => (
              <li
                key={t.id}
                className="flex items-center justify-between gap-3 text-sm py-1.5 px-2 bg-amber-50/60 border border-amber-100 rounded-sm"
                data-testid={`pew-never-row-${t.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate">{t.full_name}</div>
                  <div className="text-[11px] text-slate-500 truncate font-mono">{t.email}</div>
                </div>
                <button
                  type="button"
                  onClick={() => resendWelcome(t)}
                  disabled={!!busy[t.id]}
                  data-testid={`pew-resend-${t.id}`}
                  className="btn-outline-dark text-[11px] px-2 py-1 flex items-center gap-1 disabled:opacity-50 shrink-0"
                  title="Generate a fresh temp password + re-send the welcome email"
                >
                  <Mail className="w-3 h-3" />
                  {busy[t.id] ? "Sending…" : "Resend"}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="text-xs text-green-700 bg-green-50 border border-green-100 rounded-sm px-3 py-2 flex items-center gap-2">
          <UserCheck className="w-3.5 h-3.5" />
          Every tenant has signed in at least once. Great onboarding hygiene.
        </div>
      )}
    </section>
  );
}

function StatPill({ label, sub, value, tone, icon, testid }) {
  const cls =
    tone === "good" ? "bg-green-50 text-green-700 border-green-200" :
    tone === "warn" ? "bg-amber-50 text-amber-700 border-amber-200" :
    tone === "bad"  ? "bg-red-50 text-red-600 border-red-200" :
                      "bg-slate-50 text-slate-500 border-slate-200";
  return (
    <div
      className={`border rounded-sm px-3 py-2 ${cls}`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 opacity-80">
        {icon} {label}
      </div>
      <div className="text-xl font-bold font-mono leading-none mt-1">{value}</div>
      <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>
    </div>
  );
}
